import type { IDateState } from '../types';
import { YearMonthUi } from './YearMonthUi';
import { DateRangeEnd } from '../enums';
import { getSecondTuesday } from '../../../../../../../util/date/getSecondTuesday';
import { addDays, endOfDay, startOfDay, subDays } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';

/**
 * A UI that lets you pick a date more easily by the second tuesday
 * in a given month/year.
 */

export const UpdateTuesdaySelector = ({
  date,
  setDate: setDateExternal,
  dateRangeEnd,
}: IUpdateTuesdaySelector) => {
  const setDate = makeUpdateTuesdaySetter(setDateExternal, dateRangeEnd);

  return <YearMonthUi {...{ date, setDate }} />;
};

const makeUpdateTuesdaySetter =
  (setDate: IDateState['setDate'], dateRangeEnd: DateRangeEnd | undefined) =>
  (value: Date | undefined) => {
    if (!value || !dateRangeEnd) {
      setDate(value);
      return;
    }

    const date = getSecondTuesday(value);
    const redmond = utcToZonedTime(date, 'America/Los_Angeles');

    const modifiedDate = (() => {
      // We actually want a couple day window around patch tuesday
      // to account for any cases where the release is delayed.
      if (dateRangeEnd === DateRangeEnd.start) {
        return startOfDay(subDays(redmond, 1));
      } else if (dateRangeEnd === DateRangeEnd.end) {
        return endOfDay(addDays(redmond, 2));
      }
    })()!;

    const final = zonedTimeToUtc(modifiedDate, 'America/Los_Angeles');

    setDate(final);
  };

interface IUpdateTuesdaySelector extends IDateState {
  dateRangeEnd?: DateRangeEnd;
}
